/**
 * Respostas rápidas (pedido da PoolTrack, 02/10: "assim como no Blip, mensagens
 * rápidas padronizadas"). Texto pronto que o atendente insere no compositor da
 * conversa — pelo `/atalho` digitado ou pelo botão "Respostas rápidas" — para
 * REVISAR antes de enviar. Inserir nunca envia.
 *
 * O corpo aceita variáveis `{assim}`, preenchidas na hora da inserção com o que
 * a conversa e o ticket dela sabem. Duas delas vêm do VOCABULÁRIO do tenant
 * (spec 017): o identificador do recurso e o cliente. No rastreamento veicular
 * elas são `{placa}` e `{associacao}`; no genérico, `{identificador}` e
 * `{cliente}`. Por isso nenhuma das duas é escrita aqui: o token nasce do termo
 * (`variableToken(terms.resourceId)`), e a guarda de vocabulário do SPA segue
 * valendo para este arquivo.
 *
 * Variável que não existe, ou que não tem valor nesta conversa, fica no texto
 * COMO ESTAVA (`{placa}`), nunca vazia: "Olá, , tudo bem?" sai calado; `{nome}`
 * na caixa de texto o atendente vê antes de enviar.
 */
import { z } from 'zod';
import { Dateish, IdCoerce } from './common';
import { capitalize, type ResourceSource, type Terms } from './profiles';
import { firstNameOf, resolveDispatchParams } from './ticket-dispatch';
import type { TicketSheetSlots } from './ticket-sheet';

// ── Limites ─────────────────────────────────────────────────────────────────
export const QUICK_REPLY_TITLE_MAX = 80;
export const QUICK_REPLY_SHORTCUT_MAX = 32;
/**
 * O texto de uma mensagem de WhatsApp vai até 4.096 caracteres. 2.000 deixa
 * folga para os valores das variáveis e ainda é muito mais que uma resposta
 * pronta precisa.
 */
export const QUICK_REPLY_BODY_MAX = 2000;

/** atalho: minúsculas e números, com `-` ou `_` entre eles (`bom-dia`, `pix_2`) */
export const QUICK_REPLY_SHORTCUT_RE = /^[a-z0-9]+(?:[-_][a-z0-9]+)*$/;

/** minúsculo e sem acento — a mesma dobra das buscas do SPA */
function fold(text: string): string {
  return text.normalize('NFD').replace(/[̀-ͯ]/g, '').toLowerCase();
}

/**
 * Sugestão de atalho a partir de um texto ("Saudação inicial" →
 * "saudacao-inicial"): sem acento, minúsculo, o resto vira `-`. A tela usa
 * enquanto o admin digita; a API não transforma nada além da caixa — atalho fora
 * do formato é 422.
 */
export function slugifyShortcut(text: string): string {
  return fold(text)
    .replace(/[^a-z0-9_-]+/g, '-')
    .replace(/[-_]{2,}/g, (run) => run[0]!)
    .replace(/^[-_]+/, '')
    .slice(0, QUICK_REPLY_SHORTCUT_MAX)
    .replace(/[-_]+$/, '');
}

// ── Contratos ───────────────────────────────────────────────────────────────
const Ref = z.object({ id: z.number().int(), name: z.string() });

export const QuickReplySchema = z.object({
  id: z.number().int(),
  title: z.string(),
  shortcut: z.string(),
  body: z.string(),
  /** null = vale para todas as mesas */
  desk: Ref.nullable(),
  active: z.boolean(),
  createdBy: Ref.nullable(),
  createdAt: Dateish,
  updatedAt: Dateish,
});
export type QuickReply = z.infer<typeof QuickReplySchema>;

const TitleField = z.string().trim().min(1).max(QUICK_REPLY_TITLE_MAX);
const ShortcutField = z
  .string()
  .trim()
  .toLowerCase()
  .min(1)
  .max(QUICK_REPLY_SHORTCUT_MAX)
  .regex(QUICK_REPLY_SHORTCUT_RE, 'atalho: letras minúsculas sem acento, números, - ou _');
// o corpo guarda a formatação do WhatsApp (`*negrito*`) e as quebras de linha;
// só as pontas em branco saem
const BodyField = z.string().trim().min(1).max(QUICK_REPLY_BODY_MAX);

export const QuickReplyCreateBodySchema = z
  .object({
    title: TitleField,
    shortcut: ShortcutField,
    body: BodyField,
    deskId: z.number().int().positive().nullish(),
    active: z.boolean().optional(),
  })
  .strict();
export type QuickReplyCreateBody = z.infer<typeof QuickReplyCreateBodySchema>;

export const QuickReplyUpdateBodySchema = z
  .object({
    title: TitleField.optional(),
    shortcut: ShortcutField.optional(),
    body: BodyField.optional(),
    /** null = passa a valer para todas as mesas */
    deskId: z.number().int().positive().nullable().optional(),
    active: z.boolean().optional(),
  })
  .strict();
export type QuickReplyUpdateBody = z.infer<typeof QuickReplyUpdateBodySchema>;

/**
 * GET /chat/quick-replies (a tela de configuração): `q` busca no atalho e no
 * título; `desk_id` = as da mesa E as globais; `active=1` só as ativas.
 */
export const QuickReplyListQuerySchema = z.object({
  q: z.string().trim().max(100).optional(),
  desk_id: IdCoerce.optional(),
  active: z
    .enum(['true', 'false', '1', '0'])
    .transform((v) => v === 'true' || v === '1')
    .optional(),
});
export type QuickReplyListQuery = z.infer<typeof QuickReplyListQuerySchema>;

/** GET /chats/:id/quick-replies — `q` busca no atalho e no título */
export const ChatQuickRepliesQuerySchema = z.object({
  q: z.string().trim().max(100).optional(),
});

// ── Variáveis ───────────────────────────────────────────────────────────────
export const QUICK_REPLY_VARIABLE_KEYS = [
  'first_name',
  'name',
  'attendant',
  'ticket',
  'resource_id',
  'client',
] as const;
export type QuickReplyVariableKey = (typeof QUICK_REPLY_VARIABLE_KEYS)[number];

export interface QuickReplyVariable {
  key: QuickReplyVariableKey;
  /** o que se escreve entre chaves no corpo, sem as chaves */
  token: string;
  /** rótulo da lista do editor, no vocabulário do tenant */
  label: string;
  /** valor de exemplo da pré-visualização */
  sample: string;
}

export const QuickReplyVariableValueSchema = z.object({
  key: z.enum(QUICK_REPLY_VARIABLE_KEYS),
  token: z.string(),
  label: z.string(),
  /** null = esta conversa não tem o dado; o `{token}` fica no texto */
  value: z.string().nullable(),
});
export type QuickReplyVariableValue = z.infer<typeof QuickReplyVariableValueSchema>;

/** O que o compositor recebe de uma conversa: as respostas dela e os valores. */
export const ChatQuickRepliesSchema = z.object({
  replies: z.array(QuickReplySchema),
  variables: z.array(QuickReplyVariableValueSchema),
});
export type ChatQuickReplies = z.infer<typeof ChatQuickRepliesSchema>;

/**
 * Forma canônica de um token: sem acento, minúsculo, e o que não é letra ou
 * número vira `_` — `{Primeiro nome}`, `{primeiro_nome}` e `{PRIMEIRO-NOME}` são
 * a mesma variável, e o termo "associação" vira o token `associacao`.
 */
export function variableToken(raw: string): string {
  return fold(raw)
    .replace(/[^a-z0-9]+/g, '_')
    .replace(/^_+|_+$/g, '');
}

/** as quatro que não dependem do vertical, na ordem da lista do editor */
const FIXED_TOKENS = {
  first_name: 'primeiro_nome',
  name: 'nome',
  attendant: 'atendente',
  ticket: 'ticket',
} as const;

/**
 * As variáveis que este tenant tem, na ordem da lista do editor. O identificador
 * do recurso só existe quando o perfil trabalha com recurso; o token derivado de
 * um termo que colida com outro já listado (um termo sobrescrito como "nome")
 * fica de fora — duas variáveis com o mesmo nome teriam um valor só.
 */
export function quickReplyVariables(profile: {
  terms: Pick<Terms, 'contact' | 'client' | 'resourceId' | 'ticket'>;
  resourceSource: ResourceSource;
}): QuickReplyVariable[] {
  const { terms } = profile;
  const list: QuickReplyVariable[] = [
    {
      key: 'first_name',
      token: FIXED_TOKENS.first_name,
      label: `Primeiro nome do ${terms.contact}`,
      sample: 'Maria',
    },
    {
      key: 'name',
      token: FIXED_TOKENS.name,
      label: `Nome do ${terms.contact}`,
      sample: 'Maria Souza',
    },
    {
      key: 'attendant',
      token: FIXED_TOKENS.attendant,
      label: 'Seu primeiro nome (quem atende)',
      sample: 'Ana',
    },
    {
      key: 'ticket',
      token: FIXED_TOKENS.ticket,
      label: `Número do ${terms.ticket}`,
      sample: '1234',
    },
  ];
  const derived: QuickReplyVariable[] = [];
  if (profile.resourceSource !== 'none') {
    derived.push({
      key: 'resource_id',
      token: variableToken(terms.resourceId),
      label: `${capitalize(terms.resourceId)} do ${terms.ticket}`,
      sample: 'ABC1D23',
    });
  }
  derived.push({
    key: 'client',
    token: variableToken(terms.client),
    label: `${capitalize(terms.client)} do ${terms.ticket}`,
    sample: 'Empresa Exemplo',
  });
  for (const v of derived) {
    if (v.token !== '' && !list.some((x) => x.token === v.token)) list.push(v);
  }
  return list;
}

// ── Preenchimento ───────────────────────────────────────────────────────────
/** `{…}` numa linha só, curto — chave solta ou bloco de código não casa */
const TOKEN_RE = /\{([^{}\n]{1,40})\}/g;

/**
 * Preenche o corpo com os valores. Variável conhecida COM valor vira o valor;
 * conhecida sem valor, ou desconhecida, fica exatamente como foi escrita e entra
 * em `unresolved` (sem repetição) — é o que a tela mostra antes do envio. Uma
 * passada só: valor que contém `{nome}` não é preenchido de novo.
 */
export function fillQuickReply(
  body: string,
  variables: ReadonlyArray<{ token: string; value: string | null }>
): { text: string; unresolved: string[] } {
  const values = new Map<string, string>();
  for (const v of variables) {
    const value = v.value?.trim() ?? '';
    if (value !== '') values.set(v.token, value);
  }
  const unresolved: string[] = [];
  const text = body.replace(TOKEN_RE, (match: string, inner: string) => {
    const value = values.get(variableToken(inner));
    if (value !== undefined) return value;
    if (!unresolved.includes(match)) unresolved.push(match);
    return match;
  });
  return { text, unresolved };
}

/** Os `{…}` que ainda estão num texto (o aviso do compositor), sem repetição. */
export function unresolvedVariables(text: string): string[] {
  const found: string[] = [];
  for (const m of text.matchAll(TOKEN_RE)) if (!found.includes(m[0])) found.push(m[0]);
  return found;
}

// ── Valores de uma conversa ─────────────────────────────────────────────────
export interface QuickReplyContext {
  /** o nome da pessoa da conversa, já decidido (ver a API) — null = sem nome */
  contactName: string | null;
  /** nome de quem está atendendo (o usuário logado) */
  attendantName: string | null;
  /** o ticket da conversa, quando ela tem um */
  ticket: {
    ticketNumber: number;
    title: string;
    description: string | null;
    clientName: string | null;
    equipmentName: string | null;
    /** a origem e o solicitante: o identificador segue a regra do disparo (resolveDispatchIdentity) */
    createdVia: string | null;
    requestorName: string | null;
    /** a ficha editada (spec 031): a placa da pessoa vence, como no disparo */
    sheet: Pick<TicketSheetSlots, 'name' | 'plate'>;
  } | null;
  /** o cliente gravado na própria conversa */
  chatClientName: string | null;
  resourceSource: ResourceSource;
}

const clean = (v: string | null | undefined): string | null => {
  const t = v?.trim() ?? '';
  return t === '' ? null : t;
};

/**
 * O identificador do recurso pela MESMA regra do disparo (spec 025;
 * resolveDispatchIdentity): a placa do título `PLACA-CIDADE-NOME`, na falta a da
 * ficha na descrição e, só no ticket aberto à mão no ERP, o recurso ligado com
 * cara de identificador — em maiúsculas, sem hífen. Equipamento: o cadastrado
 * no ticket.
 */
function resourceIdValue(ctx: QuickReplyContext): string | null {
  if (!ctx.ticket) return null;
  if (ctx.resourceSource === 'equipment') return clean(ctx.ticket.equipmentName);
  if (ctx.resourceSource !== 'ticket_identifier') return null;
  const r = resolveDispatchParams([{ source: 'plate' }], {
    ticketNumber: ctx.ticket.ticketNumber,
    title: ctx.ticket.title,
    description: ctx.ticket.description,
    clientName: ctx.ticket.clientName,
    createdVia: ctx.ticket.createdVia,
    requestorName: ctx.ticket.requestorName,
    equipmentName: ctx.ticket.equipmentName,
    sheet: ctx.ticket.sheet,
  });
  return r.ok ? (r.values[0] ?? null) : null;
}

/**
 * O valor de cada variável numa conversa. Precedência escrita, não `??` solto:
 * - nome e primeiro nome: o da pessoa da conversa (quem decide é a API, pela
 *   regra da spec 028) — o primeiro nome com só a inicial maiúscula;
 * - cliente: o do TICKET, que é o que o atendente está tratando; sem ticket, o
 *   da conversa;
 * - identificador do recurso: só do ticket.
 */
export function quickReplyValues(
  ctx: QuickReplyContext
): Record<QuickReplyVariableKey, string | null> {
  return {
    first_name: firstNameOf(ctx.contactName),
    name: clean(ctx.contactName),
    attendant: firstNameOf(ctx.attendantName),
    ticket: ctx.ticket ? String(ctx.ticket.ticketNumber) : null,
    resource_id: resourceIdValue(ctx),
    client: clean(ctx.ticket?.clientName) ?? clean(ctx.chatClientName),
  };
}

/** As variáveis do tenant com o valor desta conversa — o que a API devolve. */
export function quickReplyVariableValues(
  variables: readonly QuickReplyVariable[],
  values: Record<QuickReplyVariableKey, string | null>
): QuickReplyVariableValue[] {
  return variables.map((v) => ({
    key: v.key,
    token: v.token,
    label: v.label,
    value: values[v.key],
  }));
}

// ── Busca ───────────────────────────────────────────────────────────────────
/**
 * Filtra e ordena pelo que foi digitado depois da `/` (ou na busca): atalho
 * igual, atalho que começa assim, palavra do título que começa assim, e depois
 * o que só contém. Sem acento e sem caixa. Sem busca, por atalho.
 */
export function filterQuickReplies<T extends { shortcut: string; title: string }>(
  list: readonly T[],
  query: string
): T[] {
  const q = fold(query.trim().replace(/^\//, ''));
  const byShortcut = (a: T, b: T) => a.shortcut.localeCompare(b.shortcut);
  if (q === '') return [...list].sort(byShortcut);
  const rank = (r: T): number => {
    const shortcut = r.shortcut;
    const title = fold(r.title);
    if (shortcut === q) return 0;
    if (shortcut.startsWith(q)) return 1;
    if (title.split(/[^a-z0-9]+/).some((w) => w.startsWith(q))) return 2;
    if (shortcut.includes(q)) return 3;
    if (title.includes(q)) return 4;
    return -1;
  };
  return list
    .map((r) => ({ r, k: rank(r) }))
    .filter((x) => x.k >= 0)
    .sort((a, b) => a.k - b.k || byShortcut(a.r, b.r))
    .map((x) => x.r);
}
